import React from "react";
import { Edge, Node, NodeProps, useReactFlow } from "reactflow";
import { faCloudArrowUp, faTimes } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import axios from "axios";
import { operationType } from "../../../../../services/types";
import { webURL } from "../../../../../services/API";
import { Main, Styled } from "./style";
import { store } from '../../../../../redux/store';
import { addUpdateNodeToQueue } from "../../../../../services/method";
import { allowedFileTypesDocSearch } from "./docSearch";
import { SettingsBlock, settingsDataType } from "../../../../settingsBlock";
import { IoCheckmarkCircleOutline, IoWarningOutline } from "react-icons/io5";
import { ExpandedBlock } from "../../../expanded";

export const allowedFileTypesFileUploader = [
   'image/jpg', 'image/jpeg','image/png', 'image/gif', 'image/webp',
   'audio/mp3', 'audio/mpeg', 'audio/wav',
   'video/mp4', 'video/x-msvideo', ...allowedFileTypesDocSearch
];

type uploaderProps = NodeProps<operationType> & {
   setChildMinimize?: (fn: (id: string) => void) => void;
   setChildSettings?: (fn: (id: string) => void) => void;
};

export function FileUploader({ id, data, setChildMinimize, setChildSettings }: uploaderProps) {
   const { setNodes } = useReactFlow();
   const inputRef = React.useRef<HTMLInputElement>(null);
   const [isDragFile, setIsDragFile] = React.useState(false);
   const [settings, setSettings] = React.useState<settingsDataType>({ isToggle: false });
   const [isExpanded, setIsExpanded] = React.useState(false);
   
   // --
   React.useEffect(() => {
      if (setChildMinimize) setChildMinimize(() => { setIsExpanded(prev => !prev) });
      if (setChildSettings) setChildSettings(() => { setSettings(prev => ({ ...prev, isToggle: !prev.isToggle })) });
   // eslint-disable-next-line react-hooks/exhaustive-deps
   }, [setChildMinimize, setChildSettings]);

   // ---
   const handleFileChange = (event: React.ChangeEvent<HTMLInputElement> | React.DragEvent<HTMLDivElement>) => {
      const maxFileSize = 100 * 1024 * 1024; // 100 MB in bytes
      let files: File[] = [];

      if (event.type === 'drop') { 
         files = Array.from((event as React.DragEvent<HTMLDivElement>).dataTransfer.files);
         
      } else if (event.type === 'change') {
         const changeEvent = event as React.ChangeEvent<HTMLInputElement>;
         if (changeEvent.target.files) files = Array.from(changeEvent.target.files);
      }

      const arr = files.filter((file) => allowedFileTypesFileUploader.includes(file.type) && file.size <= maxFileSize);
      if (arr.length) setNodes((nodes) =>
         nodes.map((node) => {
            if (node.id === id) return { ...node, data: { ...node.data, value: { ...node.data.value, current: node.data.value?.current ? [...node.data.value.current, ...arr] : arr} }};
            else return node;
         })
      ); 
      if (isDragFile) setIsDragFile(false);
   }

   // ---
   const handleRemove = (index: number, isFromSave: boolean) => {
      if (inputRef.current) inputRef.current.value = '';

      if (isFromSave) {
         setNodes((nodes) => nodes.map((node) => {
            if (node.id === id) {
               const arr = [...node.data.value.files];
               arr.splice(index, 1);
               return { ...node, data: { ...node.data, value: { ...node.data.value, files: arr }}};
            } else return node;
         }));
      } else {
         setNodes((nodes) => nodes.map((node) => {
            if (node.id === id) {
               const arr = [...node.data.value.current];
               arr.splice(index, 1);
               return { ...node, data: { ...node.data, value: { ...node.data.value, current: arr }}};
            } else return node;
         }));
      }
   }

   // ---
   const isNoFiles = !data.value?.current?.length && !data.value?.files?.length;
   const isWarning = isNoFiles;

   return (<>
      <SettingsBlock nodeId={ id }data={ settings } setData={ setSettings } />
      <Main>
         { isWarning ? <>
            <div id="warning"></div>
            <span id="warningIcon"><IoWarningOutline size={21} /></span>
         </> : data.output ? <>
            <div id="ran"></div>
            <span id="ranIcon"><IoCheckmarkCircleOutline size={21} /></span>
         </> : null}
         { isExpanded ? 
         <ExpandedBlock
            data={{ id, ...data }} 
            handleClose={ () => setIsExpanded(false) } 
            isWarning={ isWarning }
            style={{ width: '350px' }}
         ><Styled className="uploader">
            <div 
               className={`upload ${ isDragFile ? 'focus' : '' }`}
               onDragOver={ _=> { if (!isDragFile) setIsDragFile(true) }}
               onDragLeave={ _=> setIsDragFile(false) }
               onDrop={ handleFileChange }
            >
               <FontAwesomeIcon icon={ faCloudArrowUp } />
               <span>Click to Upload</span>
               <input ref={ inputRef } type="file" onChange={ handleFileChange } multiple accept={ allowedFileTypesFileUploader.join(',') } />
            </div>
            <div className="files">
               <div className="title" style={{ display: isNoFiles ? 'block' : 'flex'}}>
                  <div>
                     <h4>Files:</h4>
                     { isNoFiles ? <span>There is nothing</span> : null }
                  </div>
                  <span className="limit">(100MB limit per file)</span>
               </div>
               { data.value?.current?.length || data.value?.files?.length ? <ul>
                  { data.value?.current?.map((item: File, index: number) => (
                     <li key={ index.toString() }>
                        <div className="name">
                           <span>{ item.name }</span>
                           <span className="remove" onClick={ _=> handleRemove(index, false) }><FontAwesomeIcon icon={ faTimes } /></span>
                        </div>
                     </li>
                  ))}
                  { data.value?.files?.map((item: { type: string, name: string, url: string }, index: number) => (
                     <li key={ index.toString() } className="green">
                        <div className="name">
                           <span>{ item.name }</span>
                           <span className="remove" onClick={ _=> handleRemove(index, true) }><FontAwesomeIcon icon={ faTimes } /></span>
                        </div>
                     </li>
                  ))}
               </ul> : null }
            </div>
         </Styled></ExpandedBlock> : null}
      </Main>
   </>);
}

// ---
export const handleFileUploaderRun = async ({ id, nodes, setNodes, edges, isNeedValue } : {
   id: string, nodes: Node<any>[], setNodes: (payload: Node<any>[] | ((nodes: Node<any>[]) => Node<any>[])) => void, 
   edges: Edge<any>[], isNeedValue?: boolean, blockIdNeedValue?: string
}) => {
   const item = nodes.find(node => node.id === id) as Node<operationType>;

   if ((!item.data.value?.current && !item.data.value?.files) || item.data.value.isTemporaryMemory) return '';
   const sources: operationType[] = [];

   for (const edge of edges) {
      if (edge.source === item.id) {
         for (const node of nodes) {
            if (node.id === edge.target) {
               sources.push({ id: node.id, ...node.data });
            }
         }
      }
   }
   if (!sources.length || sources.some(item => item.executeRun) || !item.data.value.current.length) return '';

   // Upload ALL queued files sequentially
   const uploadedFiles: { type: string; name: string; url: string }[] = [];
   for (const currentFile of item.data.value.current) {
      const request = new FormData();
      request.append('file', currentFile);
      const response = (await axios.post(`${webURL}/general/uploader`, request, { headers: { 'Content-Type': 'multipart/form-data', 'x-auth-token': store.getState().user.data?.token }})).data;
      uploadedFiles.push({ type: currentFile.type, name: currentFile.name, url: response.url });
   }

   // Merge newly uploaded files with previously saved files
   const save = item.data.value?.files ? [...item.data.value.files, ...uploadedFiles] : uploadedFiles;
   item.data.value.files = save;
   item.data.value.isTemporaryMemory = true;

   addUpdateNodeToQueue(setNodes, (nodes) => nodes.map((node) => {
      if (item.id === node.id) {
         return { ...node, data: { ...node.data, value: { files: save, current: [], isTemporaryMemory: true }}};
      } else return node;
   }));
   if (isNeedValue) return JSON.stringify(save, null, 2);
}